import React, { useState } from "react";
import {
  ComposedChart,
  Line,
  LineChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Tabs, Tab, Skeleton } from "@mui/material";
import NMSProcessed from "./AnalyticsCharts/NMSProcessedChart";
import NMSEarningsChart from "./AnalyticsCharts/NMSEarningsChart";
import StagesChart from "./AnalyticsCharts/StagesChart";
import UserListChart from "./AnalyticsCharts/UserListChart";
import PendingEngagementChart from "./AnalyticsCharts/PendingEngagementChart";
import EngagementChart from "./AnalyticsCharts/EngagementChart";
import FollowUpChart from "./AnalyticsCharts/FollowUpChart";
import InterventionChart from "./AnalyticsCharts/InterventionChart";
import useTitanApp from "../../../../../../hooks/useTitanApp";

export default function ServiceAnalyticsCharts() {
  const { appState, pharmacyServiceState } = useTitanApp();
  const { darkMode } = appState || {};
  const {
    isPharmacyServiceProcessedLoading,
    isPharmacyServiceEarningLoading,
    isPharmacyServiceUsersLoading,
    isPharmacyServiceStagesLoading,
    serviceAnalyticsProcessed,
    serviceAnalyticsEarning,
    serviceAnalyticsStages,
  } = pharmacyServiceState || {};

  const totalServicesCompleted = serviceAnalyticsProcessed?.reduce(
    (sum, entry) => sum + entry.servicesInterventionCompleted,
    0
  );
  const totalFollowUpServicesCompleted = serviceAnalyticsProcessed?.reduce(
    (sum, entry) => sum + entry.servicesFollowUpCompleted,
    0
  );
  const totalServicesDeclined = serviceAnalyticsProcessed?.reduce(
    (sum, entry) => sum + entry.servicesDeclined,
    0
  );
  const totalEarning = serviceAnalyticsEarning?.reduce(
    (sum, entry) => sum + entry.earning,
    0
  );
  const totalPotentialEarning = serviceAnalyticsEarning?.reduce(
    (sum, entry) => sum + entry.potentialEarning,
    0
  );

  const pendingEngagement = serviceAnalyticsStages?.filter(
    (item) => item.stage === 1
  );
  const pendingEngagementCounts = pendingEngagement?.reduce(
    (sum, entry) => sum + entry.servicesCount,
    0
  );

  const Engagement = serviceAnalyticsStages?.filter((item) => item.stage === 2);
  const EngagementCounts = Engagement?.reduce(
    (sum, entry) => sum + entry.servicesCount,
    0
  );

  const FollowUp = serviceAnalyticsStages?.filter((item) => item.stage === 4);
  const FollowUpCounts = FollowUp?.reduce(
    (sum, entry) => sum + entry.servicesCount,
    0
  );

  const Intervention = serviceAnalyticsStages?.filter(
    (item) => item.stage === 3
  );
  const InterventionCounts = Intervention?.reduce(
    (sum, entry) => sum + entry.servicesCount,
    0
  );

  const [value, setValue] = useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const renderTabContent = () => {
    switch (value) {
      case 0:
        return (
          <div className="service_analytics">
            <PendingEngagementChart />

            <div className="chart-stages">
              <div>
                <h6>{pendingEngagementCounts}</h6>
                <span className="title-heading">Eligible</span>
              </div>

              <div className="analytic-button">
                <span className="title-heading">
                  {pendingEngagementCounts}%
                </span>
              </div>
            </div>
          </div>
        );
      case 1:
        return (
          <div className="service_analytics">
            <EngagementChart />

            <div className="chart-stages">
              <div>
                <h6>{EngagementCounts}</h6>
                <span className="title-heading">Awaiting consent</span>
              </div>

              <div className="analytic-button">
                <span className="title-heading">{EngagementCounts}%</span>
              </div>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="service_analytics">
            <InterventionChart />

            <div className="chart-stages">
              <div>
                <h6>{InterventionCounts}</h6>
                <span className="title-heading">Intervention</span>
              </div>

              <div className="analytic-button">
                <span className="title-heading">{InterventionCounts}%</span>
              </div>
            </div>
          </div>
        );
      case 3:
        return (
          <div className="service_analytics">
            <FollowUpChart />

            <div className="chart-stages">
              <div>
                <h6>{FollowUpCounts}</h6>
                <span className="title-heading">Follow Up</span>
              </div>

              <div className="analytic-button">
                <span className="title-heading">{FollowUpCounts}%</span>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="analytics_charts_wrapper">
      <div className="service_analytics">
        {isPharmacyServiceProcessedLoading ? (
          <Skeleton
            sx={{
              bgcolor: darkMode ? "grey.900" : "",
            }}
            height={50}
            width={120}
            animation="wave"
          />
        ) : (
          <div className="chart-heading">
            <span className="title-heading">NMS Processed</span>
          </div>
        )}

        <br></br>
        {isPharmacyServiceProcessedLoading ? (
          <Skeleton
            sx={{
              bgcolor: darkMode ? "grey.900" : "",
            }}
            animation="wave"
            height={450}
          />
        ) : (
          <>
            <div className="analytics_chart">
              <NMSProcessed />
            </div>
            <div className="chart-buttons">
              <div className="analytic-desc">
                <div className="d-flex">
                  <h6>{totalServicesCompleted}</h6>
                  <span className="title-heading">Intervention Completed</span>
                </div>
              </div>
              <div className="analytic-desc">
                <div className="d-flex">
                  <h6>{totalFollowUpServicesCompleted}</h6>
                  <span className="title-heading">Followup Completed</span>
                </div>
              </div>
              <div className="analytic-desc">
                <div className="d-flex">
                  <h6 className="declined-count">{totalServicesDeclined}</h6>
                  <span className="chart_description">Declined</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="service_analytics">
        <div>
          {isPharmacyServiceEarningLoading ? (
            <Skeleton
              sx={{
                bgcolor: darkMode ? "grey.900" : "",
              }}
              height={50}
              width={120}
              animation="wave"
            />
          ) : (
            <div className="chart-heading">
              <span className="title-heading">NMS Earnings</span>
            </div>
          )}

          <br></br>
          {isPharmacyServiceEarningLoading ? (
            <Skeleton
              sx={{
                bgcolor: darkMode ? "grey.900" : "",
              }}
              animation="wave"
              height={450}
            />
          ) : (
            <>
              <div className="analytics_chart">
                <NMSEarningsChart />
              </div>
              <div className="chart-buttons">
                {/* <div className="analytic-desc">
                  <div className="d-flex">
                    <h6>£{totalEarning}</h6>
                    <span className="chart_description">Earnings</span>
                  </div>
                  <span className="title-heading">{totalEarning}%</span>
                </div> */}
                <div className="analytic-desc">
                  <div className="d-flex">
                    <h6 className="declined-count">£{totalPotentialEarning}</h6>
                    <span className="chart_description">
                      Potential Earnings
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="service_analytics">
        <div className="analytics_chart">
          {isPharmacyServiceUsersLoading ? (
            <Skeleton
              sx={{
                bgcolor: darkMode ? "grey.900" : "",
              }}
              height={50}
              width={120}
              animation="wave"
            />
          ) : (
            <div className="chart-heading">
              <span className="title-heading">Users</span>
              <div className="analytics_stages_filter"></div>
            </div>
          )}
        </div>
        {isPharmacyServiceUsersLoading ? (
          <Skeleton
            sx={{
              bgcolor: darkMode ? "grey.900" : "",
            }}
            animation="wave"
            height={450}
          />
        ) : (
          <UserListChart />
        )}
      </div>

      <div className="service_analytics">
        <div className="analytics_chart">
          {isPharmacyServiceStagesLoading ? (
            <Skeleton
              sx={{
                bgcolor: darkMode ? "grey.900" : "",
              }}
              height={50}
              width={120}
              animation="wave"
            />
          ) : (
            <div className="chart-heading">
              <span className="title-heading">Stages</span>
            </div>
          )}

          {isPharmacyServiceStagesLoading ? (
            <Skeleton
              sx={{
                bgcolor: darkMode ? "grey.900" : "",
              }}
              animation="wave"
              height={450}
            />
          ) : (
            <div className="stages_tabs_container">
              <Tabs
                value={value}
                onChange={handleChange}
                aria-label="Tabs"
                TabIndicatorProps={{
                  className: "tab_indicator_line",
                  style: {
                    backgroundColor: "#000",
                    marginTop: "20",
                    marginLeft: "20",
                  },
                  tabbartabstyle: {
                    width: "auto",
                    padding: 0,
                    marginRight: 32,
                  },
                }}
              >
                <Tab
                  label={
                    <span
                      // className={`tabs_text notification-serv`}
                      className={`tabs_text notification-serv ${
                        value === 0 ? "selected" : ""
                      }`}
                    >
                      Eligible
                      <div className="notification-count">
                        <p>{pendingEngagementCounts}</p>
                      </div>
                    </span>
                  }
                />

                <Tab
                  label={
                    <span
                      // className={`tabs_text notification-serv`}
                      className={`tabs_text notification-serv ${
                        value === 1 ? "selected" : ""
                      }`}
                    >
                      Awaiting consent
                      <div className="notification-count">
                        <p>{EngagementCounts}</p>
                      </div>
                    </span>
                  }
                />
                <Tab
                  label={
                    <span
                      className={`tabs_text notification-serv ${
                        value === 2 ? "selected" : ""
                      }`}
                    >
                      Intervention
                      <div className="notification-count">
                        <p>{InterventionCounts}</p>
                      </div>
                    </span>
                  }
                />
                <Tab
                  label={
                    <span
                      className={`tabs_text notification-serv ${
                        value === 3 ? "selected" : ""
                      }`}
                    >
                      Follow Up
                      <div className="notification-count">
                        <p>{FollowUpCounts}</p>
                      </div>
                    </span>
                  }
                />
              </Tabs>

              {renderTabContent()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
